// MilkdownInput — a Milkdown-backed WYSIWYG markdown editor for the chat
// input box. The editor owns its DOM (ProseMirror under the hood), so
// React doesn't manage the contenteditable directly; we mirror its
// content out to the parent via onChange. We also pass a `resetSignal`
// the parent can bump (incrementing a number) when it wants the editor
// to clear — e.g. after a send. Recreating the <Milkdown> via React key
// is the simplest reliable way to reset Milkdown's internal doc.
//
// Keyboard contract matches the previous textarea:
//   - Enter        → submit (calls onSubmit)
//   - Shift+Enter  → newline (default editor behavior, no submit)
//
// IMPORTANT — why onSubmit carries the markdown instead of the parent
// reading its own state: `markdownUpdated` is debounced by 200ms inside
// @milkdown/plugin-listener (lodash `debounce(handler, 200)`, trailing
// edge). So `onChange` — and therefore the parent's mirrored state — lags
// the editor by up to 200ms. Pressing Enter inside that window submitted
// a copy of the text that was missing whatever had been typed since the
// last flush, silently truncating the message. Submitting reads the
// editor's live doc instead, which is the only source of truth that is
// never behind.
import React, { useCallback, useRef } from 'react';
import { Editor, rootCtx, defaultValueCtx, editorViewCtx, serializerCtx } from '@milkdown/core';
import { commonmark } from '@milkdown/preset-commonmark';
import { listener, listenerCtx } from '@milkdown/plugin-listener';
import { nord } from '@milkdown/theme-nord';
import { Milkdown, MilkdownProvider, useEditor } from '@milkdown/react';
import '@milkdown/theme-nord/style.css';

interface InnerProps {
  value: string;
  onChange: (markdown: string) => void;
  // Receives the editor's live markdown. Callers must use this rather
  // than whatever `onChange` last delivered — see the note above.
  onSubmit: (markdown: string) => void;
  disabled?: boolean;
}

// MilkdownInner is the actual editor — useEditor must be called inside
// MilkdownProvider, so we split this into an outer/inner pair.
const MilkdownInner: React.FC<InnerProps> = ({ value, onChange, onSubmit, disabled }) => {
  // Capture the latest callbacks in refs so the useEditor closure (run
  // once at mount) always calls the freshest handler. Without this,
  // onSubmit would be permanently bound to the first render's handler.
  const onChangeRef = useRef(onChange);
  const onSubmitRef = useRef(onSubmit);
  onChangeRef.current = onChange;
  onSubmitRef.current = onSubmit;

  const { get } = useEditor((root) =>
    Editor.make()
      .config(nord)
      .config((ctx) => {
        ctx.set(rootCtx, root);
        ctx.set(defaultValueCtx, value);
        ctx.get(listenerCtx).markdownUpdated((_, md) => {
          onChangeRef.current(md);
        });
      })
      .use(commonmark)
      .use(listener),
  );

  // Serialize the live ProseMirror doc. Synchronous and never debounced,
  // so it sees characters typed microseconds ago.
  const readMarkdown = useCallback((): string | null => {
    const editor = get();
    if (!editor) return null;
    try {
      return editor.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        return ctx.get(serializerCtx)(view.state.doc);
      });
    } catch {
      // Before the editor finishes mounting the ctx lookups throw. Null
      // tells the caller to fall back rather than send an empty message.
      return null;
    }
  }, [get]);

  const submit = useCallback(() => {
    const live = readMarkdown();
    onSubmitRef.current(live ?? '');
  }, [readMarkdown]);

  // Intercept Enter (without Shift) at the container — Milkdown's
  // ProseMirror handles editing keys internally, so we have to stop the
  // event before it reaches the editor's keymap to make Enter submit.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // `isComposing` lives on the *native* KeyboardEvent, not on React's
    // synthetic wrapper. Reading it there means the Enter that confirms an
    // IME candidate (Japanese, Chinese, Korean input) commits the text
    // instead of sending a half-typed message. Until the axios conversion
    // this read went through the synthetic event, where it is always
    // undefined, so the guard never fired.
    const composing = e.nativeEvent.isComposing;
    if (e.key === 'Enter' && !e.shiftKey && !composing) {
      e.preventDefault();
      e.stopPropagation();
      submit();
    }
  };

  // Match the `Text` component (lib/web/ui/Text.tsx) aesthetic: transparent
  // background, 1px dashed border, monospace, 0.5rem 1rem padding. The
  // Bootstrap `form-control` styling is dropped because it fights the
  // dashed-border look. `of-themed-input` is kept as the marker class
  // siblings (Text, Combobox) use so any future global styles attach
  // uniformly.
  return (
    <div
      onKeyDownCapture={onKeyDown}
      className={`milkdown-input of-themed-input ${disabled ? 'pe-none opacity-50' : ''}`}
      style={{
        background: 'transparent',
        border: '1px dashed var(--bs-border-color, #666)',
        color: 'var(--bs-body-color, inherit)',
        fontFamily: 'monospace',
        padding: '0.5rem 1rem',
        minHeight: '4.5rem',
        maxHeight: '20rem',
        overflowY: 'auto',
      }}
    >
      <Milkdown />
    </div>
  );
};

export interface MilkdownInputProps {
  // The text currently in the editor. Used as the initial value; the
  // editor owns subsequent state and reports edits via onChange.
  value: string;
  // Mirrors edits out for things that can tolerate lag (enabling the send
  // button, restoring a queued draft). Debounced 200ms by Milkdown — do
  // not use it to decide *what to send*.
  onChange: (markdown: string) => void;
  // Called with the editor's live markdown at the moment of submission.
  onSubmit: (markdown: string) => void;
  // resetSignal: bump this number to force a fresh editor instance,
  // discarding whatever the user had typed. Used after a successful send.
  resetSignal: number;
  disabled?: boolean;
}

export const MilkdownInput: React.FC<MilkdownInputProps> = (props) => {
  return (
    <MilkdownProvider>
      <MilkdownInner
        key={props.resetSignal}
        value={props.value}
        onChange={props.onChange}
        onSubmit={props.onSubmit}
        disabled={props.disabled}
      />
    </MilkdownProvider>
  );
};

export default MilkdownInput;
